import React, { useState, useEffect, useRef, useCallback } from "react";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { ReactComponent as CloseIcon } from "logic/src/components/icons/close.svg";
import { ReactComponent as UploadCircleIcon } from "logic/src/components/icons/UploadCircle.svg";
import styles from "./index.module.scss";

interface VideoThumbnailProps {
  file: File;
  thumbnailUrl?: string;
  onDelete?: () => void;
  loading?: boolean;
  error?: boolean;
}

const thumbnailCache = new Map<string, string>(); // 缓存已处理的视频缩略图

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  file,
  thumbnailUrl,
  onDelete,
  loading,
  error,
}) => {
  const [thumbnail, setThumbnail] = useState<string | null>(
    thumbnailCache.get(file.name) || null,
  );
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  // 添加模拟进度的效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (loading) {
      setUploadProgress(0);
      // 根据文件大小计算轮询间隔和进度因子
      const getConfig = () => {
        const fileSize = file.size / (1024 * 1024); // 转换为MB
        const uploadSpeed = 2; // 2MB/s
        const totalSeconds = Math.ceil(fileSize / uploadSpeed); // 总上传时间
        const progressPerSecond = 98 / totalSeconds; // 每秒需要增加的进度（总进度98%）
        
        // 根据文件大小返回合适的更新间隔，避免更新过于频繁
        const getInterval = (size: number) => {
          if (size <= 10) return 500;      // 小文件500ms更新一次
          if (size <= 50) return 800;      // 中等文件800ms更新一次
          return 1000;                     // 大文件1秒更新一次
        };

        const interval = getInterval(fileSize);
        const factor = (progressPerSecond * interval) / 1000; // 按interval计算每次增加的进度

        return { factor, interval };
      };
      const { factor, interval } = getConfig();

      timer = setInterval(() => {
        setUploadProgress(prev => {
          const nextProgress = prev + factor;
          return Math.min(nextProgress, 98);
        });
      }, interval);
    } else {
      setUploadProgress(100);
    }
    return () => clearInterval(timer);
  }, [loading, file]);

  useEffect(() => {
    if(!loading) {
      setTimeout(() => {
        setShowProgress(false);
      }, 500);
    }
  }, [loading]);

  useEffect(() => {
    setShowProgress(uploadProgress > 0);
  }, [uploadProgress]);

  const generateThumbnail = useCallback(() => {
    if (thumbnailCache.has(file.name)) {
      setThumbnail(thumbnailCache.get(file.name)!);
      return;
    }

    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    video.crossOrigin = "anonymous";
    video.currentTime = 1;
    video.muted = true;
    video.play();

    video.onloadeddata = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth / 4;
      canvas.height = video.videoHeight / 4;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/png");
        setThumbnail(dataUrl);
        thumbnailCache.set(file.name, dataUrl); // 存入缓存
      }

      video.pause();
      URL.revokeObjectURL(video.src);
    };
  }, [file]);

  useEffect(() => {
    if (!thumbnail && !thumbnailUrl) {
      generateThumbnail();
    }
  }, [thumbnail, generateThumbnail, thumbnailUrl]);

  // 修改loading部分的渲染
  return (
    <div className={styles.container}>
      <img
        src={thumbnailUrl || thumbnail || ""}
        alt="Video Thumbnail"
        className={styles.thumbnailImage}
        style={{ maxWidth: "100%" }}
      />
      {(loading || showProgress) && (
        <div className={styles.uploading}>
          { loading && <UploadCircleIcon className={styles.uploadingIcon} /> }
          { showProgress && <div className={styles.progressText}>
            {Math.round(uploadProgress)}%
          </div> }
        </div>
      )}
      {error && (
        <div className={styles.uploading}>
          <ExclamationCircleFilled className={styles.errorIcon} />
        </div>
      )}
      <div className={styles.closeBtn} onClick={onDelete}>
        <CloseIcon className={styles.closeIcon} />
      </div>
    </div>
  );
};

export default VideoThumbnail;
